import React, { useRef, useCallback, memo, useMemo, useEffect } from "react";
import { Box, IconButton, Divider, lighten, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import type { RootState } from "../store";
import Loading from "./Loading";
import PostCard from "./PostCard";
import { Refresh } from "@mui/icons-material";
import { useRefreshPosts } from "../hooks/useRefreshPosts";
import CustomButton from "./default/CustomButton";
import Theme from "../utils/theme";

const MemoizedPostCard = memo(PostCard);
const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

interface IPostCardFeedProps {
    feedLoading: boolean;
    feedSize: string;
    username?: string;
    followedOnly?: boolean;
    setFollowedOnly?: (value: boolean) => void;
    setFeedLoading: (value: boolean) => void;
}

const PostCardFeed: React.FC<IPostCardFeedProps> = ({ feedLoading, feedSize, username, followedOnly, setFollowedOnly, setFeedLoading }) => {
    const { posts, loading, hasMore } = useTypedSelector((state) => state.posts);
    const observer = useRef<IntersectionObserver | null>(null);

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const authState = useSelector((state: RootState) => state.auth);
    const hasAccount = !!authState.account;
    const targetUsername = username ?? authState.account?.username ?? undefined;

    const refreshPosts = useRefreshPosts(setFeedLoading);

    useEffect(() => {
        refreshPosts({ targetUsername: targetUsername, followedOnly, username: authState.account?.username });
    }, [authState.account?.username, followedOnly, refreshPosts, targetUsername]);

    const lastPostRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    const lastPost = posts[posts.length - 1];
                    const cursor = lastPost?.createdAt;

                    refreshPosts({
                        replace: false,
                        cursor,
                        targetUsername: targetUsername,
                        followedOnly,
                        username: authState.account?.username,
                    });
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore, posts, refreshPosts, targetUsername, followedOnly, authState.account?.username]
    );

    const renderedPostCard = useMemo(
        () =>
            posts.map((post, index) => {
                const reference = index === posts.length - 1 ? lastPostRef : null;
                return (
                    <Box key={post._id} ref={reference} sx={{ mb: 4, width: "100%" }}>
                        <MemoizedPostCard followedOnly={followedOnly} type="preview" post={post} />
                    </Box>
                );
            }),
        [posts, lastPostRef, followedOnly]
    );

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                {!!setFollowedOnly && hasAccount && (
                    <CustomButton
                        label={"Followed Only"}
                        sx={{
                            color: "white",
                            height: "24px",
                            backgroundColor: followedOnly ? theme.palette.primary.main : lighten(theme.palette.text.secondary, 0.5),
                        }}
                        onClick={() => setFollowedOnly(!followedOnly)}
                    />
                )}

                <IconButton
                    onClick={() => refreshPosts({ targetUsername: targetUsername, followedOnly, username: authState.account?.username })}
                >
                    {feedLoading ? <Loading size="24px" /> : <Refresh />}
                </IconButton>
            </Box>

            <Divider sx={{ marginBottom: 2 }} />

            <Box sx={{ position: "relative", flexGrow: 1, height: feedSize }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }} className="feed">
                    {posts.length > 0 ? (
                        renderedPostCard
                    ) : feedLoading ? (
                        <></>
                    ) : (
                        <Typography sx={{ mt: 5, color: theme.palette.text.secondary }}>Couldn't find any spicy conversations!</Typography>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default PostCardFeed;
